import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ALL_PIECES, MAX_SIZE, MIN_SIZE, PRESETS, createGame, parseSq, sqName, validateRuleset,
  type Color, type GameRules, type Placement, type Ruleset,
} from '@ic/engine';
import { Board } from '../components/Board';
import { Chips, Field, Segmented, Stepper, Toggle } from '../components/Controls';
import { Icon, type IconName } from '../components/Icons';
import { PieceIcon } from '../components/Pieces';
import { PieceEditor } from '../components/PieceEditor';
import { api } from '../lib/auth';
import { idb, type SavedRuleset } from '../lib/idb';
import { decodeRuleset, shareLink } from '../lib/share';
import { morphTarget, useArrival } from '../lib/transition';
import { activeRuleset, persisted, settings, useStore } from '../lib/store';

const draftStore = persisted<Ruleset>('ic.editorDraft', JSON.parse(JSON.stringify(activeRuleset.get())), false);
const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v));

type Tool = 'move' | 'erase' | 'void' | 'wall' | 'reachW' | 'reachB';
type Tab = 'board' | 'pieces' | 'movement' | 'rules' | 'presets';

export function RuleEditor() {
  const draft = useStore(draftStore);
  const set = useStore(settings);
  const nav = useNavigate();
  const [q] = useSearchParams();
  const [tab, setTab] = useState<Tab>('board');
  const [tool, setTool] = useState<Tool>('move');
  const [brush, setBrush] = useState<{ type: string; color: Color } | null>(null);
  const [editing, setEditing] = useState(Object.keys(draft.pieces)[0]);
  const [saved, setSaved] = useState<SavedRuleset[]>([]);
  const [msg, setMsg] = useState('');
  const [code, setCode] = useState('');
  const arrival = useArrival();
  const morph = morphTarget(arrival === 'page');

  const update = (fn: (r: Ruleset) => void) => draftStore.set((prev) => { const n = clone(prev); fn(n); return n; });
  const setRules = (patch: Partial<GameRules>) => update((r) => { r.rules = { ...r.rules, ...patch }; });
  const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };

  const loadSaved = () => idb.all<SavedRuleset>('rulesets').then((r) => setSaved(r.sort((a, b) => b.updatedAt - a.updatedAt)));
  useEffect(() => { loadSaved(); }, []);

  // Shared links: /editor#rs=<compressed json> or /editor?code=<server short code>
  useEffect(() => {
    const h = location.hash.match(/#rs=(.+)$/);
    if (h) {
      decodeRuleset(h[1]).then((rs) => { draftStore.set(rs); flash(`Loaded shared ruleset “${rs.name}”`); history.replaceState(null, '', '/editor'); })
        .catch(() => flash('That share link is broken.'));
    }
    const c = q.get('code');
    if (c) api<{ ruleset: Ruleset }>(`/rulesets/${c}`).then((r) => { draftStore.set(r.ruleset); flash(`Loaded “${r.ruleset.name}”`); }).catch(() => flash('Unknown code.'));
  }, [q]);

  const preview = useMemo(() => createGame(draft), [draft]);
  const issues = useMemo(() => validateRuleset(draft), [draft]);
  const errors = issues.filter((i) => i.level === 'error');
  const W = draft.board.width;
  const H = draft.board.height;

  const placementAt = (sq: number) => draft.setup.findIndex((p) => p.sq === sqName(sq, W));
  const blocked = (name: string) => draft.board.voids.includes(name) || draft.board.walls.includes(name);

  function place(sq: number, type: string, color: Color) {
    const name = sqName(sq, W);
    update((r) => {
      r.board.voids = r.board.voids.filter((v) => v !== name);
      r.board.walls = r.board.walls.filter((v) => v !== name);
      r.setup = [...r.setup.filter((p) => p.sq !== name), { sq: name, type, color }];
    });
  }

  const editorHooks = {
    onSquare(sq: number) {
      const name = sqName(sq, W);
      const i = placementAt(sq);
      if (tool === 'move' && brush) return place(sq, brush.type, brush.color);
      if (tool === 'erase') return i >= 0 && update((r) => { r.setup.splice(i, 1); });
      if (tool === 'void' || tool === 'wall') {
        const key = tool === 'void' ? 'voids' : 'walls';
        const other = tool === 'void' ? 'walls' : 'voids';
        return update((r) => {
          r.setup = r.setup.filter((p) => p.sq !== name);
          r.board[other] = r.board[other].filter((v) => v !== name);
          r.board[key] = r.board[key].includes(name) ? r.board[key].filter((v) => v !== name) : [...r.board[key], name];
        });
      }
      if (tool === 'reachW' || tool === 'reachB') {
        const c = tool === 'reachW' ? 'w' : 'b';
        return update((r) => {
          const list = r.rules.reachSquares[c];
          r.rules.reachSquares[c] = list.includes(name) ? list.filter((v) => v !== name) : [...list, name];
          if (r.rules.reachSquares[c].length) r.rules.winConditions.reachSquare = true;
        });
      }
      if (i >= 0 && tool === 'move') {
        // Click a placed piece without a brush: flip its color.
        update((r) => { r.setup[i].color = r.setup[i].color === 'w' ? 'b' : 'w'; });
      }
    },
    onMovePiece(from: number, to: number) {
      const i = placementAt(from);
      if (i < 0) return;
      update((r) => {
        const p = r.setup[i];
        if (to < 0) { r.setup.splice(i, 1); return; }
        const name = sqName(to, W);
        if (blocked(name)) return;
        r.setup = r.setup.filter((x, j) => j === i || x.sq !== name);
        p.sq = name;
      });
    },
    onDropData(sq: number, data: string) {
      try { const p = JSON.parse(data) as { type: string; color: Color }; if (draft.pieces[p.type]) place(sq, p.type, p.color); } catch { /* ignore */ }
    },
  };

  const marks: Record<number, string> = {};
  for (const n of draft.rules.reachSquares.w) marks[parseSq(n, W, H)] = 'mark-reach-w';
  for (const n of draft.rules.reachSquares.b) marks[parseSq(n, W, H)] = marks[parseSq(n, W, H)] ? 'mark-reach-both' : 'mark-reach-b';

  function resize(w: number, h: number) {
    w = Math.max(MIN_SIZE, Math.min(MAX_SIZE, w));
    h = Math.max(MIN_SIZE, Math.min(MAX_SIZE, h));
    update((r) => {
      const inside = (n: string) => parseSq(n, w, h) >= 0;
      // Keep black's pieces anchored to the top edge when the height changes.
      const dy = h - r.board.height;
      r.setup = r.setup.map((p) => {
        if (p.color !== 'b' || !dy) return p;
        const y = parseInt(p.sq.slice(1), 10) + dy;
        return { ...p, sq: p.sq[0] + y };
      }).filter((p) => inside(p.sq));
      r.board = { width: w, height: h, voids: r.board.voids.filter(inside), walls: r.board.walls.filter(inside) };
      r.rules.reachSquares = { w: r.rules.reachSquares.w.filter(inside), b: r.rules.reachSquares.b.filter(inside) };
    });
  }

  function count(type: string, color: Color) { return draft.setup.filter((p) => p.type === type && p.color === color).length; }
  function addPiece(type: string, color: Color) {
    // First free square scanning from the owner's back rank forward.
    for (let i = 0; i < W * H; i++) {
      const y = color === 'w' ? Math.floor(i / W) : H - 1 - Math.floor(i / W);
      const sq = y * W + (i % W);
      const name = sqName(sq, W);
      if (!blocked(name) && placementAt(sq) < 0) return place(sq, type, color);
    }
    flash('No free square left.');
  }
  function removePiece(type: string, color: Color) {
    update((r) => {
      const idx = [...r.setup.keys()].reverse().find((i) => r.setup[i].type === type && r.setup[i].color === color);
      if (idx != null) r.setup.splice(idx, 1);
    });
  }
  function mirror() {
    update((r) => {
      const whites = r.setup.filter((p) => p.color === 'w');
      const mirrored: Placement[] = whites.map((p) => ({ ...p, color: 'b', sq: p.sq[0] + (H + 1 - parseInt(p.sq.slice(1), 10)) }));
      const taken = new Set(whites.map((p) => p.sq));
      r.setup = [...whites, ...mirrored.filter((p) => !taken.has(p.sq) && !blocked(p.sq))];
    });
  }

  async function savePreset() {
    const id = PRESETS.some((p) => p.id === draft.id) || !draft.id ? crypto.randomUUID() : draft.id;
    const rs = { ...draft, id };
    draftStore.set(rs);
    await idb.put<SavedRuleset>('rulesets', { id, ruleset: rs, updatedAt: Date.now(), synced: false });
    loadSaved();
    flash('Saved to this device (synced when you are logged in).');
  }

  const typeIds = Object.keys(draft.pieces);
  const currentDef = draft.pieces[editing] ?? draft.pieces[typeIds[0]];

  return (
    <div className={`page editor-page ${arrival ? 'page-enter' : ''}`}>
      <div className="editor-board">
        <div className={`editor-head ${morph.className}`} style={morph.style}>
          <input className="title-input" value={draft.name} onChange={(e) => update((r) => { r.name = e.target.value; })} aria-label="Ruleset name" />
          <span className="tag">{W}×{H}</span>
        </div>
        <div className="toolbar">
          {([['move', 'hand', 'Move / place'], ['erase', 'eraser', 'Erase'], ['void', 'hole', 'Hole'], ['wall', 'wall', 'Block'], ['reachW', 'target', 'White target'], ['reachB', 'target', 'Black target']] as [Tool, IconName, string][]).map(([t, i, l]) => (
            <button key={t} className={`tool ${tool === t ? 'on' : ''}`} onClick={() => { setTool(t); if (t !== 'move') setBrush(null); }}><Icon name={i} size={16} />{l}</button>
          ))}
        </div>
        <Board state={preview} orientation="w" editor={editorHooks} marks={marks} />
        <div className="palette">
          {(['w', 'b'] as Color[]).map((c) => (
            <div key={c} className="palette-row">
              {typeIds.map((t) => (
                <button
                  key={t}
                  className={`palette-piece ${brush?.type === t && brush.color === c ? 'on' : ''}`}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData('text/plain', JSON.stringify({ type: t, color: c }))}
                  onClick={() => { setTool('move'); setBrush(brush?.type === t && brush.color === c ? null : { type: t, color: c }); }}
                  title={`${draft.pieces[t].name} (${c === 'w' ? 'White' : 'Black'}): click to use as brush, or drag onto the board`}
                >
                  <PieceIcon icon={draft.pieces[t].icon} color={c} set={set.pieceSet} />
                </button>
              ))}
            </div>
          ))}
          <p className="muted small">Drag pieces onto the board, or pick one as a brush and click squares. Drag a placed piece off the board to remove it; click it (no brush) to switch its color.</p>
        </div>
      </div>

      <div className="editor-side">
        <div className="tabs">
          {(['board', 'pieces', 'movement', 'rules', 'presets'] as Tab[]).map((t) => (
            <button key={t} className={tab === t ? 'on' : ''} onClick={() => setTab(t)}>{t[0].toUpperCase() + t.slice(1)}</button>
          ))}
        </div>

        <div className="editor-panel">
          {tab === 'board' && (
            <div className="stack">
              <div className="section-card">
                <Field label="Width" hint={`Files a–${'abcdefghijklmnop'[W - 1]}`}><Stepper value={W} min={MIN_SIZE} max={MAX_SIZE} onChange={(v) => resize(v, H)} /></Field>
                <Field label="Height" hint={`Ranks 1–${H}`}><Stepper value={H} min={MIN_SIZE} max={MAX_SIZE} onChange={(v) => resize(W, v)} /></Field>
              </div>
              <div className="row gap wrap">
                <button className="btn secondary" onClick={mirror}>Mirror White → Black</button>
                <button className="btn secondary" onClick={() => update((r) => { r.setup = []; })}>Clear pieces</button>
                <button className="btn secondary" onClick={() => update((r) => { r.board.voids = []; r.board.walls = []; })}>Remove holes/blocks</button>
              </div>
              <p className="muted small"><b>Holes</b> remove a square: nothing can stand there and pieces pushed into it are destroyed. <b>Blocks</b> are walls: they stop sliding pieces, fire and pushes. Use the target tools for “reach a square” wins.</p>
              <textarea className="description" placeholder="Description (optional)" value={draft.description ?? ''} onChange={(e) => update((r) => { r.description = e.target.value; })} />
            </div>
          )}

          {tab === 'pieces' && (
            <div className="stack">
              <table className="piece-table">
                <thead><tr><th>Piece</th><th>White</th><th>Black</th><th /></tr></thead>
                <tbody>
                  {typeIds.map((t) => (
                    <tr key={t}>
                      <td><span className="mini-icon"><PieceIcon icon={draft.pieces[t].icon} color="w" set={set.pieceSet} /></span> {draft.pieces[t].name}{draft.pieces[t].royal && <span className="royal-mark" title="Royal"><Icon name="crown" size={14} /></span>}</td>
                      {(['w', 'b'] as Color[]).map((c) => (
                        <td key={c} className="counter">
                          <button className="icon-btn" onClick={() => removePiece(t, c)}>−</button>
                          <span>{count(t, c)}</span>
                          <button className="icon-btn" onClick={() => addPiece(t, c)}>+</button>
                        </td>
                      ))}
                      <td>
                        <button className="btn ghost sm" onClick={() => { setEditing(t); setTab('movement'); }}>Edit</button>
                        <button className="btn ghost sm" disabled={typeIds.length < 2} onClick={() => update((r) => {
                          delete r.pieces[t];
                          r.setup = r.setup.filter((p) => p.type !== t);
                          r.rules.promotion.choices = r.rules.promotion.choices.filter((x) => x !== t);
                        })} aria-label="Remove piece type"><Icon name="x" size={14} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="stack small">
                <span className="muted">Add a piece type:</span>
                <div className="chips">
                  {Object.values(ALL_PIECES).filter((p) => !draft.pieces[p.id]).map((p) => (
                    <button type="button" key={p.id} className="chip" onClick={() => update((r) => { r.pieces[p.id] = clone(ALL_PIECES[p.id]); })}>
                      <span className="chip-icon"><PieceIcon icon={p.icon} color="w" set={set.pieceSet} /></span>{p.name}
                    </button>
                  ))}
                  <button type="button" className="chip" onClick={() => {
                    let n = 1;
                    while (draft.pieces['custom' + n]) n++;
                    const id = 'custom' + n;
                    update((r) => { r.pieces[id] = { id, name: 'Custom ' + n, letter: 'C', icon: 'pawn', value: 3, moves: [{ kind: 'leap', vectors: [[0, 1]], max: 1, mode: 'both' }], abilities: [] }; });
                    setEditing(id);
                    setTab('movement');
                  }}>
                    <span className="chip-icon"><Icon name="plus" size={16} /></span>New custom piece
                  </button>
                </div>
              </div>
            </div>
          )}

          {tab === 'movement' && currentDef && (
            <div className="stack">
              <div className="icon-picker" role="radiogroup" aria-label="Piece to edit">
                {typeIds.map((t) => (
                  <button key={t} type="button" role="radio" aria-checked={currentDef.id === t} className={currentDef.id === t ? 'on' : ''} title={draft.pieces[t].name} onClick={() => setEditing(t)}>
                    <PieceIcon icon={draft.pieces[t].icon} color="w" set={set.pieceSet} />
                  </button>
                ))}
              </div>
              <PieceEditor def={currentDef} allTypes={Object.values(draft.pieces)} onChange={(d) => update((r) => { r.pieces[d.id] = d; })} />
            </div>
          )}

          {tab === 'rules' && <RulesForm rs={draft} setRules={setRules} />}

          {tab === 'presets' && (
            <div className="stack">
              <h3>Built-in</h3>
              <div className="preset-list">
                {PRESETS.map((p) => <button key={p.id} className="btn secondary sm" onClick={() => { draftStore.set(clone(p)); flash(`Loaded ${p.name}`); }}>{p.name}</button>)}
              </div>
              <h3>Saved on this device</h3>
              {saved.length === 0 && <p className="muted small">Nothing saved yet.</p>}
              <ul className="saved-list">
                {saved.map((s) => (
                  <li key={s.id}>
                    <button className="link-btn" onClick={() => draftStore.set(clone(s.ruleset))}>{s.ruleset.name}</button>
                    <span className="muted small">{new Date(s.updatedAt).toLocaleString()} {s.synced && <Icon name="cloud" size={14} />}</span>
                    <button className="icon-btn" onClick={() => idb.del('rulesets', s.id).then(loadSaved)} aria-label="Delete"><Icon name="x" size={14} /></button>
                  </li>
                ))}
              </ul>
              <h3>Share</h3>
              <div className="row gap wrap">
                <button className="btn secondary" onClick={async () => { await navigator.clipboard.writeText(await shareLink(draft)); flash('Link copied (works offline).'); }}>Copy share link</button>
                <button className="btn secondary" disabled={!navigator.onLine} onClick={async () => {
                  try { const r = await api<{ code: string }>('/rulesets', { body: { ruleset: draft } }); setCode(r.code); await navigator.clipboard.writeText(r.code).catch(() => {}); flash(`Short code ${r.code} copied.`); }
                  catch (e) { flash((e as Error).message); }
                }}>Get short code</button>
              </div>
              {code && <div className="code-box">Code: <b>{code}</b> · {location.origin}/editor?code={code}</div>}
              <form className="row gap" onSubmit={(e) => {
                e.preventDefault();
                const v = (new FormData(e.currentTarget).get('code') as string).trim();
                if (!v) return;
                const m = v.match(/#rs=(.+)$/);
                if (m) decodeRuleset(m[1]).then((rs) => draftStore.set(rs)).catch(() => flash('Invalid link'));
                else nav(`/editor?code=${encodeURIComponent(v)}`);
              }}>
                <input name="code" placeholder="Paste a code or share link" />
                <button className="btn secondary">Import</button>
              </form>
              <div className="row gap wrap">
                <button className="btn ghost" onClick={() => {
                  const url = URL.createObjectURL(new Blob([JSON.stringify(draft, null, 2)], { type: 'application/json' }));
                  const a = document.createElement('a');
                  a.href = url; a.download = `${draft.name.replace(/\W+/g, '-').toLowerCase() || 'ruleset'}.json`; a.click();
                  URL.revokeObjectURL(url);
                }}>Export JSON</button>
                <label className="btn ghost">Import JSON<input type="file" accept="application/json" hidden onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  try { const rs = JSON.parse(await f.text()) as Ruleset; if (rs.schema !== 1) throw new Error(); draftStore.set(rs); flash('Imported.'); } catch { flash('Not a valid ruleset file.'); }
                }} /></label>
              </div>
            </div>
          )}
        </div>

        <div className="validation">
          {issues.length === 0 && <div className="success small">Ruleset looks playable.</div>}
          {issues.map((i, k) => <div key={k} className={`${i.level} small`}>{i.message}</div>)}
        </div>
        {msg && <div className="notice">{msg}</div>}
        <div className="row gap wrap editor-actions">
          <button className="btn secondary" onClick={savePreset}><Icon name="save" size={16} /> Save preset</button>
          <button className="btn secondary" disabled={!!errors.length} onClick={() => { sessionStorage.setItem('ic.sandbox', JSON.stringify(draft)); nav('/sandbox'); }}><Icon name="flask" size={16} /> Test</button>
          <button className="btn primary" disabled={!!errors.length} onClick={() => { activeRuleset.set(clone(draft)); nav('/play/setup'); }}>Use for next game</button>
        </div>
      </div>
    </div>
  );
}

function RulesForm({ rs, setRules }: { rs: Ruleset; setRules: (p: Partial<GameRules>) => void }) {
  const r = rs.rules;
  const { pieceSet } = useStore(settings);
  const wc = (k: keyof GameRules['winConditions'], v: boolean) => setRules({ winConditions: { ...r.winConditions, [k]: v } });
  const pieceChips = (filter: (id: string) => boolean) => Object.values(rs.pieces).filter((p) => filter(p.id)).map((p) => ({
    value: p.id, label: p.name, icon: <PieceIcon icon={p.icon} color="w" set={pieceSet} />,
  }));
  return (
    <div className="rules-form">
      <h3 className="section-title">Winning</h3>
      <div className="section-card">
        <Toggle checked={r.winConditions.checkmate} onChange={(v) => wc('checkmate', v)} label="Checkmate" hint="Trap a king that is in check." />
        <Toggle checked={r.winConditions.captureAllRoyals} onChange={(v) => wc('captureAllRoyals', v)} label="Capture all kings" hint="Win once every enemy king is gone. Only with the check rule off; with it on, kings are never captured." />
        <Toggle checked={r.winConditions.captureAnyRoyal} onChange={(v) => wc('captureAnyRoyal', v)} label="Capture any king" hint="Losing a single king loses the game. Only with the check rule off." />
        <Toggle checked={r.winConditions.reachSquare} onChange={(v) => wc('reachSquare', v)} label="Reach a target square" hint="Mark targets with the target tools on the board." />
        {r.winConditions.reachSquare && (
          <Field label="Pieces that count" hint="None selected means kings only.">
            <Chips options={pieceChips(() => true)} selected={r.reachTypes} onChange={(reachTypes) => setRules({ reachTypes })} />
          </Field>
        )}
      </div>

      <h3 className="section-title">Turns and moves</h3>
      <div className="section-card">
        <Field label="Moves per turn" hint="Each player makes this many moves in a row."><Stepper value={r.movesPerTurn} min={1} max={5} onChange={(movesPerTurn) => setRules({ movesPerTurn })} /></Field>
        <Toggle checked={r.check} onChange={(check) => setRules({ check })} label="Check rule" hint="Like real chess: you may not leave a king under attack, and kings are never captured, only checkmated." />
        <Toggle checked={r.castling} onChange={(castling) => setRules({ castling })} label="Castling" />
        <Toggle checked={r.enPassant} onChange={(enPassant) => setRules({ enPassant })} label="En passant" />
        <Toggle checked={r.takebacks} onChange={(takebacks) => setRules({ takebacks })} label="Takebacks" hint="Players may ask to undo a move." />
      </div>

      <h3 className="section-title">Promotion</h3>
      <div className="section-card">
        <Toggle checked={r.promotion.enabled} onChange={(enabled) => setRules({ promotion: { ...r.promotion, enabled } })} label="Pawns promote" />
        {r.promotion.enabled && <>
          <Field label="Promotion zone" hint="Last ranks that trigger promotion.">
            <Stepper value={r.promotion.zone} min={1} max={rs.board.height - 1} suffix={r.promotion.zone === 1 ? 'rank' : 'ranks'} onChange={(zone) => setRules({ promotion: { ...r.promotion, zone } })} />
          </Field>
          <Field label="Can become">
            <Chips options={pieceChips((id) => !rs.pieces[id].royal)} selected={r.promotion.choices} onChange={(choices) => setRules({ promotion: { ...r.promotion, choices } })} />
          </Field>
        </>}
      </div>

      <h3 className="section-title">Draws</h3>
      <div className="section-card">
        <Field label="Stalemate" hint="No legal move, but not in check.">
          <Segmented small value={r.draws.stalemate} onChange={(stalemate) => setRules({ draws: { ...r.draws, stalemate } })}
            options={[{ value: 'draw', label: 'Draw' }, { value: 'loss', label: 'Stalemated side loses' }]} />
        </Field>
        <Toggle checked={r.draws.fiftyMove} onChange={(fiftyMove) => setRules({ draws: { ...r.draws, fiftyMove } })} label="50-move rule" />
        <Toggle checked={r.draws.threefold} onChange={(threefold) => setRules({ draws: { ...r.draws, threefold } })} label="Threefold repetition" />
        <Toggle checked={r.draws.insufficientMaterial} onChange={(insufficientMaterial) => setRules({ draws: { ...r.draws, insufficientMaterial } })} label="Insufficient material" />
      </div>

      <h3 className="section-title">Default clock</h3>
      <div className="section-card">
        <Field label="Minutes" hint="0 = no clock. Players can still change it on the Play page.">
          <Stepper value={Math.round(r.clock.initial / 60)} min={0} max={180} onChange={(m) => setRules({ clock: { ...r.clock, initial: m * 60 } })} />
        </Field>
        <Field label="Increment"><Stepper value={r.clock.increment} min={0} max={60} suffix="s" onChange={(increment) => setRules({ clock: { ...r.clock, increment } })} /></Field>
      </div>

      <h3 className="section-title">Ability rules</h3>
      <div className="section-card">
        <Toggle checked={r.chainReaction} onChange={(chainReaction) => setRules({ chainReaction })} label="Chain reaction" hint="Bombs caught in a blast explode too." />
        <Toggle checked={r.allowSelfRoyalBlast} onChange={(allowSelfRoyalBlast) => setRules({ allowSelfRoyalBlast })} label="Bombs may hit their own king" hint="Only with the check rule off." />
        <Toggle checked={r.scorchedEarth} onChange={(scorchedEarth) => setRules({ scorchedEarth })} label="Scorched earth" hint="Fire breath leaves burning squares behind." />
        {r.scorchedEarth && (
          <Field label="Burning lasts"><Stepper value={r.scorchDuration} min={1} max={10} suffix="rounds" onChange={(scorchDuration) => setRules({ scorchDuration })} /></Field>
        )}
        <Toggle checked={r.persistentMines} onChange={(persistentMines) => setRules({ persistentMines })} label="Mines stay after triggering" hint="They remain on the board, revealed to both players." />
      </div>
    </div>
  );
}
